/// <amd-module name="Build/Common/Artifacts/Timeline" />
import * as React from "react";

import { Build } from "Build/Client/Contracts/Build";
import { DetailsSection, DetailsSectionHeader, DetailsSectionSecondaryHeader, DetailsSectionContent } from "Build/Shared/Components/DetailsSection/DetailsSection";

import * as Resources from "Build/Common/Artifacts/Resources";

import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Tooltip } from "VSSUI/TooltipEx";

import {
    IVssComponentContext
} from "VSS/Platform/Layout";
import { format } from "VSS/Core/Util/String";
import { MenuButton, IMenuItem } from "VSSUI/Menu";

import { ArtifactsStore } from 'Store';
import { IArtifactProvider } from 'Common';

interface IArtifactSnapshotContentProps {
    store: ArtifactsStore;
}

interface IArtifactSnapshotState {
    providers: IArtifactProvider[];
    canExploreArtifacts: boolean;
}

export class ArtifactSnapshotContent extends React.Component<IArtifactSnapshotContentProps, IArtifactSnapshotState> {
    private _store: ArtifactsStore;

    constructor(props: IArtifactSnapshotContentProps, context: IVssComponentContext) {
        super(props);
        this._store = props.store;
        this.state = {
            providers: this._store.getArtifactProviders(),
            canExploreArtifacts: this._store.canExploreArtifacts()
        };
    }

    public componentDidMount() {
        this._store.addListener(this._updateArtifactsState);
    }

    public componentWillUnmount() {
        this._store.removeListener(this._updateArtifactsState);
    }

    public render() {
        return <DetailsSection
            className="details-timeline-left b-details-timeline ci-hover-behavior"
            sectionKey="build-artifacts-timeline"
            chevronProps={
                {
                    ariaLabel: Resources.AriaBuildArtifactDetailsChevron
                }
            }
            isExpandedByDefault={true}>
            <DetailsSectionHeader
                renderAsHeading={true}>
                <span>{Resources.BuildArtifacts}</span>
            </DetailsSectionHeader>
            <DetailsSectionSecondaryHeader>
                <span>
                </span>
            </DetailsSectionSecondaryHeader>
            <DetailsSectionContent>
                <ArtifactsContent
                    providers={this.state.providers}
                    canExploreArtifacts={this.state.canExploreArtifacts}
                />
            </DetailsSectionContent>
        </DetailsSection>;
    }

    private _updateArtifactsState = () => {
        this.setState({
            providers: this._store.getArtifactProviders(),
            canExploreArtifacts: this._store.canExploreArtifacts()
        } as IArtifactSnapshotState);
    }
}

interface IArtifactsContentProps {
    providers: IArtifactProvider[];
    canExploreArtifacts: boolean;
}

class ArtifactsContent extends React.Component<IArtifactsContentProps> {
    public render() {
        return <div className="build-artifacts-details-content">
            <ul
                className="artifacts-list"
                aria-label={Resources.AriaBuildArtifactsList}>
                {this.props.providers.map((provider) => {
                    return <li
                        key={provider.getArtifact().id}>
                        <ArtifactItem
                            provider={provider}
                            canExploreArtifacts={this.props.canExploreArtifacts}
                        />
                    </li>;
                })}
            </ul>
        </div>
    }
}

interface IArtifactItemProps {
    provider: IArtifactProvider;
    canExploreArtifacts: boolean;
}

interface IArtifactItemState {
    target?: HTMLButtonElement;
}

class ArtifactItem extends React.Component<IArtifactItemProps, IArtifactItemState> {
    constructor(props: IArtifactItemProps) {
        super(props);
        this.state = {};
    }

    public render() {
        const provider = this.props.provider;
        const displayOptions = provider.getDisplayOptions();
        const downloadURL = provider.getDownloadURL();
        let menuItems: IMenuItem[] = [];
        if (this.props.canExploreArtifacts && provider.supportsExploring()) {
            menuItems.push({
                id: "artifact-explore",
                text: Resources.ViewContents,
                onActivate: this._onExplore,
                iconProps: {
                    iconName: "DocumentSet"
                }
            });
        }

        if (provider.supportsDownload()) {
            menuItems.push({
                id: "artifact-download",
                text: Resources.Download,
                onActivate: this._onDownload,
                iconProps: {
                    iconName: "Download"
                }
            });
        }

        // don't hide if the menu is open
        const hiddenClassName = this.state.target ? "" : "hidden";

        return <div className="build-artifact-item flex flex-center">
            <div className="artifact-name-area flex flex-grow">
                <Icon className="artifact-icon fontSizeL" iconName={displayOptions.iconName} />
                <span>&nbsp;</span>
                <Tooltip text={format(Resources.DownloadFormat, displayOptions.text)}>
                    <div className="artifact-data flex flex-column relative">
                        {downloadURL && <Link className="artifact-name" href={downloadURL}>{displayOptions.text}</Link>}
                        {!downloadURL && <div className="artifact-name">{displayOptions.text}</div>}
                        <div className="artifact-info">{displayOptions.type}</div>
                    </div>
                </Tooltip>
            </div>
            <div className={`button-area ${hiddenClassName} relative flex-self-end`}>
                {
                    menuItems.length > 0 ? (
                        <MenuButton
                            hideDropdownIcon={true}
                            className="subtle"
                            contextualMenuProps={{
                                menuProps: {
                                    id: "timeline-menu",
                                    items: menuItems
                                }
                            }}
                            iconProps={{ iconName: "More" }}
                        />
                    ) : null
                }
            </div>

        </div>;
    }

    private _onDownload = () => {
        this.props.provider.onDownload();
    }

    private _onExplore = () => {
        this.props.provider.onExplore();
    }
}
